import type { PropsWithChildren } from "react";
import { ApolloClient, ApolloProvider, InMemoryCache, useQuery, gql } from '@apollo/client';
import Pagination from '@react/widget/Pagination';
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import { query } from "./query";
import clsx from "clsx";

const ENDPOINT = import.meta.env.PUBLIC_ENDPOINT ?? "https://stg-cms.avepoint.com/api";
const TOKEN = import.meta.env.PUBLIC_TOKEN ?? "T6t9Fa11ouec5Kkc75VeaIaPTX0yA7-h";

interface Props extends PropsWithChildren {
  currentSite: Page.Site;
  solution: string;
  industry: string;
  platform: string;
  region: string;
  category: string;
  limit: number;
  page: number;
  getCaseStudiesByPage: ({ value }: { value: number }) => void;
  label?: Record<string, string>;
}

const CaseStudyCard = ({
  currentSite,
  title,
  url,
  introTitle,
  logo,
  tags,
}: {
  currentSite: Page.Site;
  title: string;
  url: string;
  introTitle?: string;
  logo?: { src: string; width: number; height: number; alt: string };
  tags: Array<{ title: string }>;
}) => (
  <a
    key={title}
    href={url}
    className="flex flex-col items-stretch w-full bg-white rounded-lg shadow hover:shadow-xl transition duration-300 p-0 max-w-md mx-auto border"
  >
    <div className="rounded-t-lg bg-gray-50 p-10 text-center flex items-center h-[200px]">
      {logo ? (
        <img
          src={logo.src}
          alt={logo.alt}
          width={logo.width}
          height={logo.height}
          className={clsx(currentSite.locale == "fr" ? "p-8" : "max-h-[120px]", "mx-auto  transition duration-300")}
        />
      ) : (
        <span></span>
      )}
    </div>
    <div className="flex flex-col justify-between p-5 rounded-b-lg min-h-[200px]">
      <h3 className="text-xl font-semibold mb-2">{introTitle}</h3>
      <div className="mt-auto space-y-4 space-x-1">
        {tags.map((tag) => (
          <span
            key={tag.title}
            className="px-2 py-1.5 bg-gray-50 inline-block rounded text-xs text-gray-700"
          >
            {tag.title}
          </span>
        ))}
      </div>
    </div>
  </a>
);

const CaseStudiesContent = ({
  currentSite,
  solution,
  industry,
  platform,
  region,
  category,
  limit,
  page,
  getCaseStudiesByPage,
  label,
}: Props) => {
  const GET_CASESTUDIES = gql(query);
  const { loading, error, data } = useQuery(GET_CASESTUDIES, {
    variables: {
      site: currentSite.handle,
      industry,
      platforms: platform,
      region,
      relationid: solution === "*" ? "not 1" : solution,
      caseStudyCategories: category,
      limit,
      offset: (page - 1) * limit,
    },
    fetchPolicy: "network-only",
  });

  if (loading) return <p>{label?.load}...</p>;
  if (error) return <p>Error: {error.message}</p>;

  const caseStudies = data?.entries.map((cVal: any) => {
    const introTitle = cVal.caseStudy?.find(
      (block: any) => block.__typename === "caseStudy_caseStudyPreview_BlockType"
    )?.introTitle;
    const logo = cVal.caseStudy?.find(
      (block: any) => block.__typename === "caseStudy_customerDetails_BlockType"
    )?.logo?.[0];
    return {
      currentSite: currentSite,
      title: cVal.title,
      url: cVal.url,
      introTitle,
      logo,
      tags: cVal.tags,
    };
  });

  const total = Math.ceil(data?.entryCount / limit);
  //console.log("total", total);
  return total > 0 ? (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 border-t pt-12">
        {caseStudies?.map((entry: any) => (
          <CaseStudyCard key={entry.title} {...entry} />
        ))}
      </div>
      <Pagination
        label={{ prev: label?.prev || "Prev", next: label?.next || "Next" }}
        page={page}
        total={total}
        onChange={(e) => getCaseStudiesByPage({ value: e })}
      />
    </>
  ) : (
    <div className="mt-12 border-t pt-12 text-center">
      <p>{label?.nodata}</p>
    </div>
  );
};

export default function GetCaseStudies(props: Props) {
  const cache = new InMemoryCache();
  loadDevMessages();
  loadErrorMessages();

  const client = new ApolloClient({
    uri: ENDPOINT,
    headers: {
      authorization: TOKEN ? `Bearer ${TOKEN}` : "",
    },
    cache: cache
  });

  return (
    <ApolloProvider client={client}>
      <CaseStudiesContent {...props} />
    </ApolloProvider>
  );
}